import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Paper, Grid, Stack } from '@mui/material'
import useMosInfo from '../../common/provider/useMosInfo'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import WorkerRequiredButton from '../../common/WorkerRequiredButton'
import { useActiveSingleJob } from './hooks/useActiveSingleJob'

// Sub-components
import JobHeader from './components/JobHeader'
import JobControls from './components/JobControls'
import JobProgress from './components/JobProgress'
import JobTabs from './components/JobTabs'

import { isBlockedByIncidence } from '../../common/canStartJob'
import FabricationDocumentsButton from '../../common/documents/FabricationDocumentsButton'

/**
 * @typedef {Object} Material
 * @property {string} id
 * @property {string} name
 * @property {string} quantity
 */

/**
 * ActiveSingleJob Widget
 *
 * Displays detailed information about a single active job, including progress,
 * controls for reporting quantity, and tabs for materials, tools, and quality.
 * Refactored to use atomic components and custom hooks.
 *
 * @param {Object} props
 * @param {string} [props.jobTitle] Title of the job
 * @param {string} [props.jobDescription] Description of the job
 * @param {Object} [props.jobReference] Job reference object
 * @param {Object} [props.partReference] Part reference object
 * @param {Object} [props.node] Node object
 * @param {number} [props.progress] Current progress value (0-100)
 * @param {number} [props.target] Target quantity
 * @param {number} [props.completed] Completed quantity
 * @param {number} [props.nok] Quantity reported as NOK
 * @param {Material[]} [props.materials] List of materials required for the job
 * @param {function} [props.onPause] Callback when "Interrumpir" is clicked
 * @param {function} [props.onFinish] Callback when "Finalizar" is clicked
 * @param {function(number): void} [props.onReportOk] Callback when "OK" is clicked
 * @param {function(number): void} [props.onReportNok] Callback when "NOK" is clicked
 */
const ActiveSingleJob = ({
  jobTitle = 'OF-3202 · REF-002',
  jobDescription = 'Hull panel kit · JB-2202',
  jobReference = { id: 1, code: 'JOB-001', name: 'Job Default' },
  partReference,
  node,
  progress = 0,
  target = 8,
  completed = 0,
  nok = 0,
  materials = [],
  onPause,
  onFinish,
  // Story 113 (S120/S123/S124) — the two action buttons no longer have fixed labels: the resolved
  // layout arrives from the container (which reads it from the single shared point) and each side
  // dispatches its own handler. Absent, JobHeader resolves today's Pause + Finish.
  onGoToExecution,
  onChangeStatus,
  modeActions,
  // S141 (D-I6): only preparation is labelled; execution keeps today's look.
  inPreparation = false,
  onReportOk,
  onReportNok,
  counter,
  setCounter,
  handleIncrement,
  handleDecrement,
  finishDisabled,
  pauseDisabled,
  // S62 (story 86) — why Pause/Finish are disabled, when the acceptance form is the reason. Passed through
  // untouched: both containers compute it, and this component only forwards it to the buttons.
  governedByAcceptanceTooltip,
  // S79 (story 100) — Report (OK/NOK) is gated ONLY for an external-of-the-circuit job (#86 never
  // gated Report for the MOD job), so it travels as its own pair of props, forwarded to JobControls.
  reportDisabled,
  reportDisabledTooltip,
  showPartActions = false,
  onCancelParts,
  onAddParts
}) => {
  const { t_ } = useSmartTranslation()
  // Historia 148 (S11/D14): la orden de fabricación se lee del CONTEXTO, no de una prop. Las dos
  // superficies que montan este componente —`ActiveSingleJobContainer` y el maestro-detalle
  // `ActiveMultiJobContainer`— comparten ese contexto, así que una sola lectura cubre las dos y no
  // hay una segunda condición que pueda divergir (R-16).
  const { worker, globalData, fabricationOrder } = useMosInfo()
  const activeIncidence = globalData?.incidence
  // Issue #3724: Pause/Finish are gated by the "Blocks production" flag of the active incidence's type
  // (incidence pick list, Sun's incidence.blocksProduction), no longer by a hardcoded STOPPED/IDLE token list.
  const controlsDisabled = useMemo(() => isBlockedByIncidence(activeIncidence), [activeIncidence])
  const { tabValue, handleTabChange } = useActiveSingleJob()
  return (
    <>
      <Paper
        sx={{
          width: '100%',
          height: '100%',
          bgcolor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          p: 1,
          borderRadius: 1,
          overflow: 'hidden'
        }}
        elevation={0}
      >
        <JobHeader
          jobTitle={jobTitle}
          jobDescription={jobDescription}
          onPause={onPause}
          onFinish={onFinish}
          onGoToExecution={onGoToExecution}
          onChangeStatus={onChangeStatus}
          actions={modeActions}
          inPreparation={inPreparation}
          finishDisabled={finishDisabled}
          pauseDisabled={pauseDisabled}
          governedByAcceptanceTooltip={governedByAcceptanceTooltip}
        />

        {showPartActions
          ? (
            <Stack direction='row' spacing={1} sx={{ mb: 2 }}>
              <WorkerRequiredButton
                variant='outlined'
                color='error'
                sx={{ textTransform: 'none' }}
                onClick={onCancelParts}
              >
                {t_('Cancel parts')}
              </WorkerRequiredButton>
              <WorkerRequiredButton
                variant='outlined'
                color='primary'
                sx={{ textTransform: 'none' }}
                onClick={onAddParts}
              >
                {t_('Add parts')}
              </WorkerRequiredButton>
            </Stack>
            )
          : null}

        <Grid container spacing={4} sx={{ mb: 2 }}>
          <JobControls
            counter={counter}
            setCounter={setCounter}
            handleIncrement={handleIncrement}
            handleDecrement={handleDecrement}
            onReportOk={onReportOk}
            onReportNok={onReportNok}
            workerPresent={!!worker}
            controlsDisabled={controlsDisabled}
            reportDisabled={reportDisabled}
            reportDisabledTooltip={reportDisabledTooltip}
          />

          <JobProgress
            completed={completed}
            target={target}
            nok={nok}
          />
        </Grid>

        {/* Historia 148 (S8/S9/S11/S13) — el TERCER punto de MOS, y el CUARTO cuando este mismo
            componente se monta como vista de detalle del maestro-detalle. El botón ocupa el hueco
            exacto del antiguo icono «Open materials sheet», que abría la ventana documental de un
            solo tipo de recurso identificando el job por `node.parentNode.id` (ámbito documental
            TEMPORAL — corrección del as-is nº1). Aquí viaja `jobReference.id`, el job MOSTRADO en
            el detalle, que con varios jobs en lote no es el primero del lote (S10). Las tres
            pestañas y su fuente de recursos no se tocan (S9). */}
        <JobTabs
          tabValue={tabValue}
          handleTabChange={handleTabChange}
          leadingAction={
            <FabricationDocumentsButton
              fabricationOrderId={fabricationOrder?.id}
              jobReferenceId={jobReference?.id}
            />
          }
          materials={materials}
          jobReference={jobReference}
        />
      </Paper>
    </>
  )
}

ActiveSingleJob.propTypes = {
  jobTitle: PropTypes.string,
  onGoToExecution: PropTypes.func,
  onChangeStatus: PropTypes.func,
  modeActions: PropTypes.object,
  inPreparation: PropTypes.bool,
  jobDescription: PropTypes.string,
  jobReference: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    name: PropTypes.string,
    node: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    })
  }),
  partReference: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    name: PropTypes.string
  }),
  node: PropTypes.object,
  progress: PropTypes.number,
  target: PropTypes.number,
  completed: PropTypes.number,
  nok: PropTypes.number,
  materials: PropTypes.arrayOf(PropTypes.object),
  onPause: PropTypes.func,
  onFinish: PropTypes.func,
  onReportOk: PropTypes.func,
  onReportNok: PropTypes.func,
  counter: PropTypes.number,
  setCounter: PropTypes.func,
  handleIncrement: PropTypes.func,
  handleDecrement: PropTypes.func,
  finishDisabled: PropTypes.bool,
  pauseDisabled: PropTypes.bool,
  governedByAcceptanceTooltip: PropTypes.string,
  reportDisabled: PropTypes.bool,
  reportDisabledTooltip: PropTypes.string,
  showPartActions: PropTypes.bool,
  onCancelParts: PropTypes.func,
  onAddParts: PropTypes.func
}

export default ActiveSingleJob
